import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cat, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTasks } from '@/hooks/useTaskQueries';
import { format, startOfWeek, addDays } from 'date-fns';
import { he } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

const USERS = [
  { id: 'matan', name: 'מתן', color: 'hsl(var(--skylight-blue))', textColor: 'text-skylight-blue' },
  { id: 'heila', name: 'הילה', color: 'hsl(var(--skylight-pink))', textColor: 'text-skylight-pink' },
  { id: 'asaf', name: 'אסף', color: 'hsl(var(--skylight-green))', textColor: 'text-skylight-green' }
];

const HOURS = Array.from({ length: 17 }, (_, i) => i + 7); // 07:00 - 23:00

// מערך ימים באנגלית לפי אינדקס JS (0=Sunday..6=Saturday)
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// מחזיר את שם היום באנגלית לפי Date.getDay()
const getEnglishDayKey = (date: Date) => EN_DAYS[date.getDay()];

// מתרגם ערך עברי לאנגלית אם מגיע (תמיכה בנתונים מעורבים)
const normalizeDay = (value?: string | null) => {
  if (!value) return null;
  const heToEn: Record<string, string> = {
    'ראשון': 'Sunday',
    'שני': 'Monday',
    'שלישי': 'Tuesday',
    'רביעי': 'Wednesday',
    'חמישי': 'Thursday',
    'שישי': 'Friday',
    'שבת': 'Saturday',
  };
  return heToEn[value] || value;
};

export default function WeeklyCalendar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: tasks = [] } = useTasks();
  const [selectedUsers, setSelectedUsers] = useState<string[]>(USERS.map(u => u.id));
  const [weekOffset, setWeekOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState<any>(null);
  const hourRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data (using Open-Meteo free API - no key needed)
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Herzliya coordinates
        const lat = 32.1650;
        const lon = 34.8441;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weathercode&timezone=auto&forecast_days=4`
        );
        const data = await response.json();
        setWeather(data);
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      }
    };
    fetchWeather();
  }, []);

  // Auto-scroll to current hour on mount and when week changes
  useEffect(() => {
    const currentHour = new Date().getHours();
    const scrollToHour = currentHour >= 7 && currentHour <= 23 ? currentHour : 7;
    
    // Wait for render and transition to complete
    const timer = setTimeout(() => {
      const hourElement = hourRefs.current[scrollToHour];
      const scrollContainer = scrollContainerRef.current;
      
      if (hourElement && scrollContainer) {
        // Calculate the offset from the top of the scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = hourElement.getBoundingClientRect();
        const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop;
        
        scrollContainer.scrollTo({ 
          top: offset - 100, // Add some padding from top
          behavior: 'smooth' 
        });
      }
    }, isTransitioning ? 300 : 100);

    return () => clearTimeout(timer);
  }, [weekOffset, isTransitioning]);

  // Real-time subscription for tasks
  useEffect(() => {
    const channel = supabase
      .channel('weekly-calendar-tasks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        () => {
          // Refetch tasks when any change occurs
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const currentWeekStart = addDays(weekStart, weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const changeWeek = (direction: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setWeekOffset(prev => prev + direction);
      setIsTransitioning(false);
    }, 150);
  };

  const goToToday = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setWeekOffset(0);
      setIsTransitioning(false);
    }, 150);
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getTasksForDayAndHour = (dayKeyEn: string, hour: number) => {
    // המשימות מתוזמנות לפי יום בשבוע בלבד (לא תאריך ספציפי)
    // כך שהן חוזרות אוטומטית בכל שבוע באותו יום ושעה
    return tasks.filter(task => {
      const dayMatches = normalizeDay(task.scheduledDay) === dayKeyEn;
      const hourMatches = task.scheduledHour === hour;
      // כל המשימות משוייכות למתן
      return dayMatches && hourMatches && selectedUsers.includes('matan');
    });
  };

  return (
    <div className="min-h-screen bg-skylight-bg animate-fade-in" dir="rtl">
      {/* Header - Compact Design */}
      <div className="bg-white border-b border-skylight-border px-4 py-2 transition-all duration-300">
        <div className="max-w-[1600px] mx-auto">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-2">
            {/* Left: Date & Time & Weather */}
            <div className="flex items-center gap-4">
              {/* Date */}
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-medium text-skylight-text">
                  {format(currentWeekStart, 'MMM yyyy', { locale: he })}
                </h1>
                <span className="text-sm text-skylight-muted">
                  {format(currentWeekStart, 'd', { locale: he })}-{format(addDays(currentWeekStart, 6), 'd', { locale: he })}
                </span>
              </div>

              {/* Current Time */}
              <div className="flex items-center gap-1.5 px-2 py-1 bg-skylight-bg rounded border border-skylight-border">
                <div className="text-base font-medium text-skylight-text">
                  {format(currentTime, 'HH:mm', { locale: he })}
                </div>
                <div className="text-xs text-skylight-muted">
                  {format(currentTime, 'EEE', { locale: he })}
                </div>
              </div>

              {/* Weather Forecast - Compact */}
              {weather && (
                <div className="flex items-center gap-1.5">
                  {[0, 1, 2, 3].map((dayOffset) => {
                    const weatherCode = weather.daily?.weathercode?.[dayOffset];
                    const tempMax = weather.daily?.temperature_2m_max?.[dayOffset];
                    const weatherDate = addDays(new Date(), dayOffset);
                    
                    const getWeatherEmoji = (code: number) => {
                      if (code === 0) return '☀️';
                      if (code <= 3) return '⛅';
                      if (code <= 48) return '☁️';
                      if (code <= 67) return '🌧️';
                      if (code <= 77) return '🌨️';
                      if (code <= 82) return '🌧️';
                      return '⛈️';
                    };

                    return (
                      <div
                        key={dayOffset}
                        className="flex items-center gap-0.5 px-1.5 py-0.5 bg-skylight-bg rounded border border-skylight-border"
                        title={format(weatherDate, 'EEEE', { locale: he })}
                      >
                        <span className="text-sm">{getWeatherEmoji(weatherCode)}</span>
                        <span className="text-xs font-medium text-skylight-text">
                          {Math.round(tempMax)}°
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Week Navigation */}
              <div className="flex items-center gap-1 bg-skylight-bg px-2 py-1 rounded">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 hover:bg-skylight-hover"
                  onClick={() => changeWeek(1)}
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
                <button 
                  onClick={goToToday}
                  className="text-xs font-medium px-1.5 hover:text-skylight-blue transition-colors"
                >
                  השבוע
                </button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 hover:bg-skylight-hover"
                  onClick={() => changeWeek(-1)}
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
              </div>
              
              <Button 
                size="sm"
                className="bg-skylight-blue hover:bg-skylight-blue/90 text-white rounded-full h-7 px-3"
              >
                <Plus className="h-3 w-3 ml-1" />
                <span className="text-xs">אירוע</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="h-7 w-7 p-0 hover:bg-skylight-hover transition-all duration-200 group"
                title="חזרה למשימות"
              >
                <Cat className="h-3.5 w-3.5 transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12" />
              </Button>
            </div>
          </div>

          {/* Bottom Row - User Filters */}
          <div className="flex items-center gap-2">
            {USERS.map(user => (
              <button
                key={user.id}
                onClick={() => toggleUser(user.id)}
                className={`
                  flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs
                  transition-all duration-300
                  ${selectedUsers.includes(user.id) 
                    ? 'bg-white shadow-sm border border-skylight-border' 
                    : 'bg-transparent opacity-40 hover:opacity-60'
                  }
                `}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: user.color }}
                />
                <span className="font-medium">{user.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="max-w-[1600px] mx-auto px-4 pb-2">
        <div className={`
          bg-white rounded-lg shadow-sm border border-skylight-border overflow-hidden
          transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}
        `}>
          <div className="h-[calc(100vh-140px)] overflow-y-auto scroll-smooth" ref={scrollContainerRef}>
          {/* Days Header */}
          <div className="grid grid-cols-8 border-b border-skylight-border sticky top-0 bg-white z-10">
            <div className="p-6"></div>
            {weekDays.map((day, i) => {
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
              return (
                <div key={i} className="p-6 text-center border-r border-skylight-border transition-all duration-300">
                  <div className="text-xs font-medium text-skylight-muted uppercase tracking-wider mb-2">
                    {format(day, 'EEE', { locale: he })}
                  </div>
                  <div className={`
                    text-2xl font-light transition-all duration-300
                    ${isToday ? 'text-skylight-blue font-medium scale-110' : 'text-skylight-text'}
                  `}>
                    {format(day, 'd', { locale: he })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Slots */}
          <div className="divide-y divide-skylight-border">
            {HOURS.map(hour => {
              const isCurrentHour = new Date().getHours() === hour;
              return (
                <div 
                  key={hour} 
                  ref={el => hourRefs.current[hour] = el}
                  className="grid grid-cols-8 min-h-[100px]"
                >
                  <div className={`p-4 border-l border-skylight-border transition-all duration-300 ${
                    isCurrentHour 
                      ? 'bg-skylight-blue/10 border-r-4 border-r-skylight-blue' 
                      : 'bg-skylight-bg/50'
                  }`}>
                    <div className={`text-sm font-light text-center transition-all duration-300 ${
                      isCurrentHour 
                        ? 'text-skylight-blue font-medium scale-110' 
                        : 'text-skylight-muted'
                    }`}>
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                  </div>
                {weekDays.map((day, dayIndex) => {
                  const dayKeyEn = getEnglishDayKey(day); // למטרות סינון
                  const dayNameHe = format(day, 'EEEE', { locale: he }); // לשימוש בתצוגה
                  const dayTasks = getTasksForDayAndHour(dayKeyEn, hour);
                  
                  return (
                    <div
                      key={dayIndex}
                      className="p-3 border-r border-skylight-border hover:bg-skylight-hover/30 transition-all duration-200 cursor-pointer relative group"
                    >
                      {dayTasks.map(task => {
                        const user = USERS.find(u => u.id === 'matan');
                        return (
                          <div
                            key={task.id}
                            className="mb-2 p-3 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.02] animate-fade-in cursor-pointer"
                            style={{
                              backgroundColor: `${user?.color}15`,
                              borderRight: `3px solid ${user?.color}`
                            }}
                             title={`${task.hebrewDescription || task.description || ''}\nחוזר בכל שבוע ב${dayNameHe}`}
                          >
                            <div className={`font-medium text-sm mb-1 ${user?.textColor}`}>
                              {task.hebrewTitle || task.title}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-xs text-skylight-muted">
                                {hour.toString().padStart(2, '0')}:
                                {(task.scheduledMinute || 0).toString().padStart(2, '0')}
                              </div>
                              {task.isCompleted && (
                                <div className="text-xs font-medium text-skylight-green">✓</div>
                              )}
                            </div>
                            {task.coins > 0 && (
                              <div className="text-xs text-skylight-muted mt-1">
                                🪙 {task.coins}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-110">
                        <Plus className="h-4 w-4 text-skylight-muted" />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            })}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
